import React from "react";

function safeNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function HistoryStatusBadge({ status }) {
  const normalized = String(status || "").toUpperCase();

  const statusMap = {
    APLICADA: {
      label: "Aplicada",
      className: "border-emerald-200 bg-emerald-50 text-emerald-700",
    },
    PULADA: {
      label: "Pulada",
      className: "border-amber-200 bg-amber-50 text-amber-800",
    },
    PENDENTE: {
      label: "Pendente",
      className: "border-indigo-200 bg-indigo-50 text-indigo-700",
    },
    PROGRAMADA: {
      label: "Programada",
      className: "border-indigo-200 bg-indigo-50 text-indigo-700",
    },
  };

  const current = statusMap[normalized] || {
    label: normalized || "Registrado",
    className: "border-gray-200 bg-gray-50 text-gray-600",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider ${current.className}`}
    >
      {current.label}
    </span>
  );
}

export default function DoseHistoryList({
  doses = [],
  showHistory = false,
  setShowHistory,
  getId,
  getDoseFlask,
  formatDateTimeBR,
  formatPtNumber,
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold font-lora text-[--primary-deep]">
          Histórico de Doses
        </h3>

        <button
          type="button"
          onClick={() => setShowHistory((prev) => !prev)}
          className="text-xs font-bold uppercase tracking-wider text-purple-800 hover:text-purple-950 transition-colors cursor-pointer"
        >
          {showHistory ? "Ocultar histórico" : `Exibir histórico (${doses.length})`}
        </button>
      </div>

      {showHistory && (
        <>
          {doses.length === 0 ? (
            <div className="p-5 bg-gray-50/80 border border-dashed border-purple-200/60 rounded-3xl text-center">
              <p className="text-xs font-medium text-gray-500">Nenhum registro no histórico de doses.</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {doses.map((dose) => {
                const id = getId(dose);
                const flask = getDoseFlask(dose);

                const medName =
                  flask?.name ||
                  flask?.medicationName ||
                  dose?.medicationName ||
                  dose?.medication_name ||
                  dose?.name ||
                  "Medicamento";

                const brand =
                  flask?.brand ||
                  flask?.packagingLabel ||
                  dose?.packagingLabel ||
                  dose?.brand ||
                  "";

                const medType = String(flask?.medicationType || "").toUpperCase();
                const nameLower = String(medName).toLowerCase();
                const unitLabel = String(flask?.unitLabel || "").toLowerCase();
                const mgPerUnit = safeNumber(flask?.mgPerUnit ?? flask?.mgBase);

                let dosageLabel = `${formatPtNumber(dose?.dosage, 2)} mg`;

                if (
                  medType === "PO" ||
                  medType === "PÓ" ||
                  nameLower.includes("glutamina") ||
                  nameLower.includes("creatina") ||
                  nameLower.includes("whey") ||
                  nameLower.includes("colageno")
                ) {
                  if (mgPerUnit && dose?.dosage) {
                    const scoops = Math.round(dose.dosage / mgPerUnit) || 1;
                    dosageLabel = `${scoops} dose(s)`;
                  } else {
                    dosageLabel = "1 dose";
                  }
                } else if (medType === "COMPRIMIDO" || medType === "CAPSULA") {
                  if (mgPerUnit && dose?.dosage) {
                    const count = Math.round(dose.dosage / mgPerUnit) || 1;
                    dosageLabel = `${count} ${medType === "CAPSULA" ? "cápsula(s)" : "comprimido(s)"}`;
                  } else if (unitLabel === "mcg" || (dose?.dosage > 0 && dose?.dosage < 1)) {
                    const mcgVal = dose.dosage < 1 ? Math.round(dose.dosage * 1000 * 100) / 100 : dose.dosage;
                    dosageLabel = `${formatPtNumber(mcgVal, 2)} mcg`;
                  }
                }

                const status = String(dose?.status || "").toUpperCase();

                return (
                  <div
                    key={id}
                    className="glass-card rounded-2xl p-4 transition-all duration-200 border border-purple-100/60 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-sm font-extrabold text-[#2A1F45] truncate">
                            {medName}
                          </h4>

                          {brand && (
                            <span className="text-[11px] font-medium text-gray-500 truncate">
                              • {brand}
                            </span>
                          )}
                        </div>

                        <div className="mt-1.5 flex items-center justify-between gap-2 flex-wrap text-xs">
                          <span className="font-semibold text-gray-700">
                            {formatDateTimeBR(dose?.date, dose?.scheduleTime)}
                          </span>

                          <span className="font-extrabold text-purple-900 bg-purple-50 px-2.5 py-0.5 rounded-lg border border-purple-100">
                            {dosageLabel}
                          </span>
                        </div>
                      </div>

                      <HistoryStatusBadge status={status} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </section>
  );
}